import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import SubmitButton from "@/components/ui/submit-button";
import { Switch } from "@/components/ui/switch";
import Typography from "@/components/ui/typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationStatus } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

const editCardSchema = z
	.object({
		exp_month: z
			.string()
			.min(2, "Required")
			.max(2, "Must be 2 digits")
			.refine(
				(val) => {
					const num = Number(val);
					return !Number.isNaN(num) && num >= 1 && num <= 12;
				},
				{ message: "Month must be between 01 and 12" },
			),
		exp_year: z
			.string()
			.min(2, "Required")
			.max(2, "Must be 2 digits")
			.refine(
				(val) => {
					const currentYear = new Date().getFullYear() % 100; // last two digits
					const num = Number(val);
					return !Number.isNaN(num) && num >= currentYear;
				},
				{ message: "Year must not be in the past" },
			),
		default: z.boolean(),
	})
	.refine(
		(data) => {
			const currentDate = new Date();
			const currentYear = currentDate.getFullYear() % 100; // last two digits
			const currentMonth = currentDate.getMonth() + 1; // 1-based
			const year = Number(data.exp_year);
			const month = Number(data.exp_month);
			if (year === currentYear) {
				return month >= currentMonth;
			}
			return true;
		},
		{
			message: "Month must not be in the past for the current year",
			path: ["exp_month"],
		},
	);

export type EditCardFormValues = z.infer<typeof editCardSchema>;

interface Props {
	open: boolean;
	setOpen: (open: boolean) => void;
	initialValues: Omit<EditCardFormValues, "default"> & {
		default: boolean | 0 | 1;
	};
	onSubmit: (values: EditCardFormValues) => void;
	updateStatus: MutationStatus;
}

function EditCardPopup({
	open,
	setOpen,
	updateStatus,
	initialValues,
	onSubmit,
}: Props) {
	// Convert initialValues.default to boolean if it's 0 or 1
	const normalizedInitialValues: EditCardFormValues = {
		...initialValues,
		default: initialValues.default === 1 || initialValues.default === true,
	};

	const form = useForm<EditCardFormValues>({
		resolver: zodResolver(editCardSchema),
		defaultValues: normalizedInitialValues,
	});

	const handleSubmit = form.handleSubmit((values) => {
		onSubmit(values);
	});

	const handleCancel = () => {
		form.reset(normalizedInitialValues);
		setOpen(false);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(open) => {
				form.reset(normalizedInitialValues);
				setOpen(open);
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						<Typography variant={"h4"}>Edit Card</Typography>
					</DialogTitle>
					<DialogDescription>
						<Typography variant={"p"}>Edit your card details below.</Typography>
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="">
							<FormField
								control={form.control}
								name="exp_month"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Exp. Month</FormLabel>
										<FormControl>
											<Input type="text" className="w-full" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="exp_year"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Exp. Year</FormLabel>
										<FormControl>
											<Input type="text" className="w-full" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<FormField
							control={form.control}
							name="default"
							render={({ field }) => (
								<FormItem>
									<div className="flex items-center gap-2">
										<FormControl>
											<Switch
												id="default"
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<FormLabel htmlFor="default" className="mb-0">
											Set as default card
										</FormLabel>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<Button
								size={"md"}
								variant="outline"
								type="button"
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<SubmitButton
								mutationStatus={updateStatus}
								size={"md"}
								type="submit"
								variant="default"
							>
								Save
							</SubmitButton>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}

export default EditCardPopup;
